import StackTrace from 'stacktrace-js';
import { safeJSONStringify } from './utils';

interface FormattedError {
  message: string;
  stack: string;
}

function safeToString(value: unknown): string {
  try {
    return String(value);
  } catch {
    return '<unprintable value>';
  }
}
function getErrorStack(error: Error): string {
  try {
    return safeToString(error.stack);
  } catch {
    return '<unavailable stack>';
  }
}
export async function formatStackTrace(
  error: unknown
): Promise<FormattedError> {
  let message = error ? safeToString(error) : 'Unknown error';

  let stack = '';
  if (error instanceof Error) {
    try {
      message = `${error.name}: ${error.message}`;
      const stackFrames = await StackTrace.fromError(error);
      stack = stackFrames.map((frame) => frame.toString()).join('\n');
    } catch (stackError) {
      stack = [
        `Error parsing stack trace: ${safeToString(stackError)}`,
        'Original stack trace:',
        getErrorStack(error),
      ].join('\n');
    }
  }

  return { message, stack };
}

export async function formatError(error: unknown): Promise<string> {
  try {
    const { message, stack } = await formatStackTrace(error);
    return [message, stack].filter(Boolean).join('\n');
  } catch (formattingError) {
    return [
      `Error formatting error: ${safeToString(formattingError)}`,
      `Original error: ${safeToString(error)}`,
    ].join('\n');
  }
}

export interface ParsedError {
  name: string;
  message: string;
  stack?: string;
  status?: number | string;
  code?: string;
}

export function parseError(error: unknown): ParsedError | undefined {
  if (!error) {
    return undefined;
  }
  if (typeof error === 'string') {
    return { message: error, name: 'Error' };
  }
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      name: error.name,
    };
  }
  if (typeof error === 'object') {
    const e = error as any;

    // RTKQ FetchBaseQueryError often has:
    // { status, data, error }
    const status = e.status;
    const code = e.code;

    // Common places messages live:
    const candidates: unknown[] = [
      e.message,
      e.error,
      e.data?.message,
      e.data?.error,
      e.data, // sometimes data is just a string
    ];

    const message =
      candidates.map(asString).find((s) => s && s.trim().length > 0) ??
      safeJSONStringify(e);

    const name = typeof e.name === 'string' && e.name ? e.name : 'Error';

    return { message, name, status, code };
  }
  return { message: String(error), name: 'Error' };
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (value == null) {
    return undefined;
  }
  return safeJSONStringify(value);
}
